import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Folder, FileText, Upload, Trash2, Download, Search, ChevronRight,
  Loader2, FolderOpen, File, FileSpreadsheet, Edit2, Plus, X,
} from 'lucide-react';
import { CategoryBadge } from './RequestDocsButton';
import { MergeFilesPanel } from './MergeFilesPanel';
import ClientDocsBoard from './ClientDocsBoard';
import { openAuthed } from '../lib/authSession';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (fileType) => {
  if (fileType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
  if (fileType?.includes('spreadsheet') || fileType?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  if (fileType?.includes('image')) return <File className="h-5 w-5 text-purple-500" />;
  if (fileType?.includes('word') || fileType?.includes('document')) return <FileText className="h-5 w-5 text-blue-500" />;
  return <File className="h-5 w-5 text-gray-500" />;
};

const EMPTY_FORM = { client_name: '', client_email: '', client_phone: '', notes: '', agent_id: '' };

/**
 * Admin Client Folders Tab
 * ------------------------
 * Three-pane drilldown: Agents -> Folders -> Files. Admin can create, edit,
 * delete folders for any agent, plus upload/delete/download any file.
 */
const AdminClientFoldersTab = ({ apiUrl, adminPassword }) => {
  const [agents, setAgents] = useState([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [folderSearch, setFolderSearch] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [mergeRefreshKey, setMergeRefreshKey] = useState(0);
  const fileInputRef = useRef(null);

  // Folder Create/Edit modal state
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('create'); // 'create' | 'edit'
  const [folderForm, setFolderForm] = useState(EMPTY_FORM);
  const [folderEditingId, setFolderEditingId] = useState(null);
  const [folderSaving, setFolderSaving] = useState(false);
  const [folderError, setFolderError] = useState('');

  // Delete confirmation modal state
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const toggleFileSelect = (id) => setSelectedFileIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const clearFileSelection = () => {
    setSelectedFileIds([]);
    setMergeRefreshKey(k => k + 1);
  };

  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/client-folders-overview`, {
        headers: { 'X-Admin-Password': adminPassword || '' },
      });
      if (r.ok) {
        const data = await r.json();
        setAgents(data);
      }
    } catch (e) {
      console.error('Failed to fetch agents overview:', e);
    } finally {
      setLoadingAgents(false);
    }
  }, [apiUrl, adminPassword]);

  const fetchFolders = useCallback(async (agentId) => {
    setLoadingFolders(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-folders/${agentId}`);
      if (r.ok) setFolders(await r.json());
    } catch (e) {
      console.error('Failed to fetch folders:', e);
    } finally {
      setLoadingFolders(false);
    }
  }, [apiUrl]);

  const fetchFiles = useCallback(async (folderId) => {
    setLoadingFiles(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folderId}`);
      if (r.ok) setFiles(await r.json());
    } catch (e) {
      console.error('Failed to fetch files:', e);
    } finally {
      setLoadingFiles(false);
    }
  }, [apiUrl]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);
  useEffect(() => {
    if (selectedAgent) fetchFolders(selectedAgent.id);
    else { setFolders([]); setSelectedFolder(null); setFiles([]); }
  }, [selectedAgent, fetchFolders]);
  useEffect(() => {
    if (selectedFolder) fetchFiles(selectedFolder.id);
    else setFiles([]);
    setSelectedFileIds([]);
  }, [selectedFolder, fetchFiles]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0 || !selectedFolder || !selectedAgent) return;
    setUploading(true);
    const list = Array.from(fileList);
    const failed = [];
    for (let i = 0; i < list.length; i++) {
      setUploadProgress({ done: i, total: list.length, name: list[i].name });
      const fd = new FormData();
      fd.append('files', list[i]);
      try {
        const r = await fetch(
          `${apiUrl}/api/client-files/${selectedFolder.id}?agent_id=${selectedAgent.id}&uploaded_via=admin_manual`,
          { method: 'POST', body: fd }
        );
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          failed.push(`${list[i].name}: ${typeof err.detail === 'string' ? err.detail : 'upload failed'}`);
        }
      } catch (e) {
        failed.push(`${list[i].name}: network error`);
      }
      if ((i + 1) % 3 === 0) fetchFiles(selectedFolder.id);
    }
    await fetchFiles(selectedFolder.id);
    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (failed.length) alert(`${list.length - failed.length} of ${list.length} files uploaded.\n\nFailed:\n${failed.join('\n')}`);
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      const r = await fetch(`${apiUrl}/api/client-files/file/${fileId}`, { method: 'DELETE' });
      if (r.ok) fetchFiles(selectedFolder.id);
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const handleRenameFile = async (fileId, currentName) => {
    const next = window.prompt('Rename file:', currentName || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      const r = await fetch(`${apiUrl}/api/client-files/file/${fileId}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: trimmed }),
      });
      if (r.ok) fetchFiles(selectedFolder.id);
      else alert((await r.json())?.detail || 'Rename failed.');
    } catch (e) {
      console.error('Rename failed:', e);
    }
  };

  // ---- Folder CRUD ----
  const openCreateFolder = () => {
    setFolderModalMode('create');
    setFolderEditingId(null);
    setFolderForm({ ...EMPTY_FORM, agent_id: selectedAgent?.id || '' });
    setFolderError('');
    setFolderModalOpen(true);
  };

  const openEditFolder = (folder) => {
    setFolderModalMode('edit');
    setFolderEditingId(folder.id);
    setFolderForm({
      client_name: folder.client_name || '',
      client_email: folder.client_email || '',
      client_phone: folder.client_phone || '',
      notes: folder.notes || '',
      agent_id: folder.agent_id || selectedAgent?.id || '',
    });
    setFolderError('');
    setFolderModalOpen(true);
  };

  const closeFolderModal = () => {
    setFolderModalOpen(false);
    setFolderEditingId(null);
    setFolderForm(EMPTY_FORM);
    setFolderError('');
  };

  const saveFolder = async () => {
    if (!folderForm.client_name?.trim()) {
      setFolderError('Client name is required.');
      return;
    }
    setFolderSaving(true);
    setFolderError('');
    try {
      if (folderModalMode === 'create') {
        const targetAgentId = folderForm.agent_id || selectedAgent?.id;
        if (!targetAgentId) {
          setFolderError('Please select a target agent.');
          setFolderSaving(false);
          return;
        }
        const r = await fetch(`${apiUrl}/api/client-folders/${targetAgentId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword || '' },
          body: JSON.stringify({
            client_name: folderForm.client_name.trim(),
            client_email: folderForm.client_email?.trim() || null,
            client_phone: folderForm.client_phone?.trim() || null,
            notes: folderForm.notes?.trim() || null,
          }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d?.detail || 'Create failed.');
        }
      } else {
        const payload = {
          client_name: folderForm.client_name.trim(),
          client_email: folderForm.client_email?.trim() || null,
          client_phone: folderForm.client_phone?.trim() || null,
          notes: folderForm.notes?.trim() || null,
        };
        if (folderForm.agent_id && folderForm.agent_id !== selectedAgent?.id) {
          payload.agent_id = folderForm.agent_id;
        }
        const r = await fetch(`${apiUrl}/api/client-folders/${folderEditingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword || '' },
          body: JSON.stringify(payload),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d?.detail || 'Update failed.');
        }
      }
      closeFolderModal();
      if (selectedAgent) {
        fetchFolders(selectedAgent.id);
        fetchAgents();
      }
    } catch (e) {
      setFolderError(e.message || 'Save failed.');
    } finally {
      setFolderSaving(false);
    }
  };

  const openDeleteFolder = (folder) => {
    setDeleteFolder(folder);
    setDeleteConfirmText('');
  };

  const closeDeleteFolder = () => {
    setDeleteFolder(null);
    setDeleteConfirmText('');
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolder) return;
    if (deleteConfirmText.trim() !== (deleteFolder.client_name || '').trim()) return;
    setDeleting(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-folders/${deleteFolder.id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': adminPassword || '' },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d?.detail || 'Delete failed.');
      } else {
        if (selectedFolder?.id === deleteFolder.id) setSelectedFolder(null);
        if (selectedAgent) {
          fetchFolders(selectedAgent.id);
          fetchAgents();
        }
        closeDeleteFolder();
      }
    } catch (e) {
      console.error('Delete folder failed:', e);
      alert('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAgents = agents.filter(a =>
    a.name?.toLowerCase().includes(agentSearch.toLowerCase()) ||
    a.email?.toLowerCase().includes(agentSearch.toLowerCase())
  );
  const filteredFolders = folders.filter(f =>
    f.client_name?.toLowerCase().includes(folderSearch.toLowerCase()) ||
    f.client_email?.toLowerCase().includes(folderSearch.toLowerCase())
  );

  const deleteConfirmMatches = !!deleteFolder &&
    deleteConfirmText.trim() === (deleteFolder.client_name || '').trim();

  return (
    <Card data-testid="admin-client-folders-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-[#0077B6]" />
          All Agent Client Folders
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Browse every advisor's client folders. Create, edit, or delete folders.
          Upload, download, or delete documents. Manual uploads are auto-categorised
          (Income, ID, Down Payment, Consent) and parsed for owner &amp; company.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Agents pane */}
          <div className="md:col-span-3">
            <div className="border rounded-lg overflow-hidden">
              <div className="p-3 border-b bg-gray-50">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Agents</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="admin-cf-agent-search"
                  />
                </div>
              </div>
              <div className="max-h-[560px] overflow-y-auto">
                {loadingAgents ? (
                  <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></div>
                ) : filteredAgents.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500 text-center">No agents.</p>
                ) : (
                  <div className="divide-y">
                    {filteredAgents.map(a => (
                      <button
                        key={a.id}
                        onClick={() => { setSelectedAgent(a); setSelectedFolder(null); }}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                          selectedAgent?.id === a.id ? 'bg-[#F0F7FF] border-l-4 border-[#0077B6]' : ''
                        }`}
                        data-testid={`admin-cf-agent-${a.id}`}
                      >
                        <p className="text-sm font-medium text-gray-800 truncate">{a.name}</p>
                        <p className="text-xs text-gray-500 truncate">{a.email}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {a.folder_count || 0} folder(s) • {a.file_count || 0} file(s)
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Folders pane */}
          <div className="md:col-span-3">
            <div className="border rounded-lg overflow-hidden">
              <div className="p-3 border-b bg-gray-50">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide truncate">
                    {selectedAgent ? `${selectedAgent.name}'s Clients` : 'Client Folders'}
                  </p>
                  {selectedAgent && (
                    <Button
                      onClick={openCreateFolder}
                      size="sm"
                      className="bg-[#0077B6] h-7 px-2 text-xs"
                      data-testid="admin-cf-new-folder-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      New
                    </Button>
                  )}
                </div>
                {selectedAgent && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search clients..."
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="admin-cf-folder-search"
                    />
                  </div>
                )}
              </div>
              <div className="max-h-[560px] overflow-y-auto">
                {!selectedAgent ? (
                  <p className="p-6 text-sm text-gray-400 text-center">Select an agent to view folders.</p>
                ) : loadingFolders ? (
                  <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></div>
                ) : filteredFolders.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500 text-center">No client folders.</p>
                ) : (
                  <div className="divide-y">
                    {filteredFolders.map(f => (
                      <div
                        key={f.id}
                        className={`group flex items-start gap-2 p-3 hover:bg-gray-50 transition-colors ${
                          selectedFolder?.id === f.id ? 'bg-[#F0F7FF] border-l-4 border-[#0077B6]' : ''
                        }`}
                      >
                        <button
                          onClick={() => setSelectedFolder(f)}
                          className="flex-1 text-left min-w-0"
                          data-testid={`admin-cf-folder-${f.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <Folder className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-800 truncate">{f.client_name}</p>
                          </div>
                          {f.client_email && <p className="text-xs text-gray-500 truncate ml-6">{f.client_email}</p>}
                        </button>
                        <div className="flex-shrink-0 flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditFolder(f); }}
                            className="p-1 text-gray-500 hover:text-[#0077B6]"
                            title="Edit folder"
                            data-testid={`admin-cf-edit-folder-${f.id}`}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteFolder(f); }}
                            className="p-1 text-red-500 hover:text-red-700"
                            title="Delete folder"
                            data-testid={`admin-cf-delete-folder-${f.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Files pane */}
          <div className="md:col-span-6">
            <div className="border rounded-lg overflow-hidden">
              <div className="p-3 border-b bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {selectedFolder ? (
                    <>
                      <Folder className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                      <p className="text-sm font-semibold text-gray-800 truncate">{selectedFolder.client_name}</p>
                      <span className="text-xs text-gray-500">({files.length} file{files.length === 1 ? '' : 's'})</span>
                    </>
                  ) : (
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Files</p>
                  )}
                </div>
                {selectedFolder && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => handleUpload(e.target.files)}
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      size="sm"
                      className="bg-[#0077B6]"
                      data-testid="admin-cf-upload-btn"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                      {uploadProgress ? <span data-testid="upload-progress">Uploading {uploadProgress.done + 1}/{uploadProgress.total}…</span> : 'Upload'}
                    </Button>
                  </>
                )}
              </div>
              <div
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (selectedFolder && e.dataTransfer?.types?.includes('Files')) setIsDraggingOver(true); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (selectedFolder && e.dataTransfer?.types?.includes('Files')) setIsDraggingOver(true); }}
                onDragLeave={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setIsDraggingOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setIsDraggingOver(false);
                  const dropped = e.dataTransfer?.files;
                  if (dropped && dropped.length > 0) handleUpload(dropped);
                }}
                className={`relative max-h-[520px] overflow-y-auto transition-all ${isDraggingOver ? 'ring-2 ring-[#0077B6] ring-inset bg-blue-50/40' : ''}`}
                data-testid="admin-cf-dropzone"
              >
                {isDraggingOver && selectedFolder && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                    <div className="flex items-center gap-3 text-[#0077B6] font-semibold">
                      <Upload className="h-6 w-6" />
                      Drop to upload to {selectedFolder.client_name}
                    </div>
                  </div>
                )}
                {!selectedFolder ? (
                  <div className="py-16 text-center text-gray-400">
                    <ChevronRight className="h-10 w-10 mx-auto mb-2" />
                    <p className="text-sm">Select a client folder to view files.</p>
                  </div>
                ) : loadingFiles ? (
                  <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
                ) : files.filter(f => !f.is_merged_package).length === 0 ? (
                  <div className="py-16 text-center text-gray-500">
                    <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No files in this folder.</p>
                    <p className="text-xs text-gray-400 mt-1">Drag and drop files here, or use Upload.</p>
                  </div>
                ) : (
                  <div className="p-3">
                    <ClientDocsBoard
                      apiUrl={apiUrl}
                      folderId={selectedFolder.id}
                      files={files}
                      onRefresh={() => fetchFiles(selectedFolder.id)}
                      onRename={handleRenameFile}
                      onDelete={handleDeleteFile}
                      onMerged={() => { setMergeRefreshKey(k => k + 1); fetchFiles(selectedFolder.id); }}
                    />
                  </div>
                )}
              </div>
              {/* Merge panel */}
              {selectedFolder && (
                <div className="p-3 border-t bg-gray-50">
                  <MergeFilesPanel
                    apiUrl={apiUrl}
                    folderId={selectedFolder.id}
                    folder={selectedFolder}
                    agentName={selectedAgent?.name}
                    selectedFileIds={selectedFileIds}
                    onClearSelection={clearFileSelection}
                    refreshKey={mergeRefreshKey}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>

      {/* Create/Edit Folder Modal */}
      {folderModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeFolderModal}
          data-testid="admin-cf-folder-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {folderModalMode === 'create' ? 'New Client Folder' : 'Edit Client Folder'}
              </h3>
              <button onClick={closeFolderModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={folderForm.client_name}
                  onChange={(e) => setFolderForm({ ...folderForm, client_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                  placeholder="John Smith"
                  data-testid="admin-cf-folder-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client Email</label>
                <input
                  type="email"
                  value={folderForm.client_email}
                  onChange={(e) => setFolderForm({ ...folderForm, client_email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                  placeholder="client@example.com"
                  data-testid="admin-cf-folder-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client Phone</label>
                <input
                  type="tel"
                  value={folderForm.client_phone}
                  onChange={(e) => setFolderForm({ ...folderForm, client_phone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                  placeholder="(555) 123-4567"
                  data-testid="admin-cf-folder-phone-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={folderForm.notes}
                  onChange={(e) => setFolderForm({ ...folderForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                  placeholder="Optional notes..."
                  data-testid="admin-cf-folder-notes-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Assign to Agent
                </label>
                <select
                  value={folderForm.agent_id || selectedAgent?.id || ''}
                  onChange={(e) => setFolderForm({ ...folderForm, agent_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                  data-testid="admin-cf-folder-agent-select"
                >
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                  ))}
                </select>
                {folderModalMode === 'edit' && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Changing the agent will move this folder (and its files) to the selected agent.
                  </p>
                )}
              </div>
              {folderError && (
                <p className="text-xs text-red-600" data-testid="admin-cf-folder-error">{folderError}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={closeFolderModal} disabled={folderSaving}>Cancel</Button>
              <Button
                onClick={saveFolder}
                disabled={folderSaving}
                className="bg-[#0077B6]"
                data-testid="admin-cf-folder-save-btn"
              >
                {folderSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {folderModalMode === 'create' ? 'Create Folder' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Modal (type-to-confirm) */}
      {deleteFolder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeDeleteFolder}
          data-testid="admin-cf-delete-folder-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Trash2 className="h-5 w-5 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Client Folder</h3>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              You are about to permanently delete the folder for{' '}
              <span className="font-semibold">{deleteFolder.client_name}</span> and{' '}
              <span className="font-semibold text-red-600">all files inside it</span>.
              This action cannot be undone.
            </p>
            <p className="text-sm text-gray-700 mb-3">
              To confirm, type the client name exactly:{' '}
              <span className="font-mono font-semibold text-gray-900">{deleteFolder.client_name}</span>
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
              placeholder="Type client name to confirm"
              autoFocus
              data-testid="admin-cf-delete-folder-confirm-input"
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={closeDeleteFolder} disabled={deleting}>Cancel</Button>
              <Button
                onClick={confirmDeleteFolder}
                disabled={!deleteConfirmMatches || deleting}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                data-testid="admin-cf-delete-folder-confirm-btn"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete Folder
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AdminClientFoldersTab;
